import { Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TOON_BOOM_ANIMATION_ORG_ID } from "../../../../constants/OrgConstants.ts";
import { getSelectedOrganization } from "../../../../helpers/localStorage.ts";
import { useAttributionReports } from "../../../../hooks/useAttributionReports.tsx";
import { useAppStore } from "../../../../hooks/useStores.tsx";
import TableLayout, { TableRow } from "../../../../layout/TableLayout/TableLayout.tsx";
import { ICatalogReport, ICatalogReportTableRow } from "../../../../models/attribution-reports.ts";
import { showErrorToast } from "../../../../utils/toast.tsx";

const CatalogReport = () => {
	const { t } = useTranslation("translation", { keyPrefix: "account.attributionReports" });
	const { attributionReportsStore } = useAppStore();
	const { createCatalogReportTableRow } = useAttributionReports();
	const [catalogReportTableData, setCatalogReportTableData] = useState<TableRow<ICatalogReportTableRow>[]>([]);
	const allTableHeaders = t("tables.catalogReport.headers", { returnObjects: true }) as string[];

	const isToonBoomOrg = useMemo(() => {
		const selectedOrg = getSelectedOrganization();
		return selectedOrg?.organization.uid === TOON_BOOM_ANIMATION_ORG_ID;
	}, []);

	// Filter headers for Toon Boom org - show only Partner name and Unique attributed images share (%)
	const tableHeaders = useMemo(() => {
		if (isToonBoomOrg) {
			return [allTableHeaders[0], allTableHeaders[6]];
		}
		return allTableHeaders;
	}, [allTableHeaders, isToonBoomOrg]);

	useEffect(() => {
		const loadData = async () => {
			if (
				!attributionReportsStore.isCatalogReportDataLoading &&
				!attributionReportsStore.catalogReportData.length
			) {
				try {
					await attributionReportsStore.loadCatalogReportByModelVersion();
				} catch (e: any) {
					showErrorToast(t("pleaseTryAgainLater"));
				}
			}
		};
		loadData();
	}, [attributionReportsStore.selectedDateRange]);

	useEffect(() => {
		attributionReportsStore.setSelectedTab(t("tabs.catalogReport"));
	}, []);

	useEffect(() => {
		if (attributionReportsStore.catalogReportData) {
			setCatalogReportTableData(
				attributionReportsStore.catalogReportData.map((data: ICatalogReport) => {
					return createCatalogReportTableRow(data, isToonBoomOrg);
				}),
			);
		}
	}, [attributionReportsStore.catalogReportData, isToonBoomOrg]);

	return (
		<>
			<TableLayout
				hideHeader={true}
				headerArray={tableHeaders}
				tableData={catalogReportTableData}
				loading={attributionReportsStore.isCatalogReportDataLoading}
				emptyStateComponent={<Typography>{t("tables.catalogReport.emptyState")}</Typography>}
			/>
		</>
	);
};
export default observer(CatalogReport);
